// The tenancy agreement shown on the applicant's page, every page in turn, rendered with pdf.js (loaded on first use, so
// the rest of the site does not carry it). The worker is bundled as a plain .js worker (?worker) — a .mjs asset is
// served with the wrong type by some servers and then fails to load.
//
// `overlay` is drawn over the last page — the Signatures page — so the tenant signs in their box on the agreement
// itself. SIGN_AT mirrors the server (agreement.service.ts TENANT_BOX_TOP and SIGNATURE_AREA): what is drawn in that
// box is what gets stamped into the PDF, in the same place.
import { useEffect, useRef, useState, type ReactNode } from 'react';

const A4 = { w: 595.28, h: 841.89 };
const BOX_TOP = A4.h - 180;
/** The tenant's signature area (above the signature line) and the date, as percentages of the page from the top left. */
const SIGN_AT = {
  signature: { left: (120 / A4.w) * 100, top: ((A4.h - (BOX_TOP - 132 + 62)) / A4.h) * 100, width: (210 / A4.w) * 100, height: (62 / A4.h) * 100 },
  date: { left: (382 / A4.w) * 100, top: ((A4.h - (BOX_TOP - 128 + 12)) / A4.h) * 100 },
  /** Just under the signature line: room for the box's own button. */
  tools: { left: (120 / A4.w) * 100, top: ((A4.h - (BOX_TOP - 138)) / A4.h) * 100, width: (210 / A4.w) * 100 },
};

export function AgreementViewer({ url, overlay }: { url: string; overlay?: ReactNode }) {
  const host = useRef<HTMLDivElement | null>(null);
  const [pages, setPages] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A new url mounts a new viewer (callers key it by url), so the state starts empty for each document.
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const PdfWorker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?worker')).default;
        if (!pdfjs.GlobalWorkerOptions.workerPort) pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error('The agreement could not be opened');
        const doc = await pdfjs.getDocument({ data: new Uint8Array(await res.arrayBuffer()) }).promise;
        // Rendered once to images at a width that stays sharp on a phone or a laptop.
        const width = Math.min(1400, Math.max(700, (host.current?.clientWidth ?? 700) * (window.devicePixelRatio || 1)));
        const out: string[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: width / base.width });
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
          await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
          // JPEG has no transparency, and some browsers (Android's WebView) leave the page's blank margins transparent,
          // which then come out black: lay the page onto white before encoding it.
          const flat = document.createElement('canvas');
          flat.width = canvas.width; flat.height = canvas.height;
          const ctx = flat.getContext('2d')!;
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, flat.width, flat.height);
          ctx.drawImage(canvas, 0, 0);
          out.push(flat.toDataURL('image/jpeg', 0.85));
          if (cancelled) return;
        }
        if (!cancelled) setPages(out);
      } catch (e) {
        console.error('agreement viewer', e);
        if (!cancelled) setError('The agreement could not be shown here');
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // Phones: the signature box is a third of a narrow page, so the last page can be enlarged (2.6×, scrolled to the box)
  // to sign with a finger. Only the page's displayed size changes — the canvas, its pixels and what is sent stay the same.
  const [zoom, setZoom] = useState(false);
  const toggleZoom = () => {
    const next = !zoom;
    setZoom(next);
    requestAnimationFrame(() => host.current?.querySelector('[data-sign-box]')?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' }));
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-ink-100/70">
      <div ref={host} className="max-h-[72vh] overflow-y-auto overscroll-contain p-2 sm:max-h-[78vh] sm:p-3">
        {error ? <p className="p-6 text-center text-[14px] text-[var(--status-critical)]">{error}. <a href={url} target="_blank" rel="noreferrer" className="underline">Open it as a PDF</a></p>
          : !pages ? <p className="flex min-h-[40vh] items-center justify-center p-10 text-center text-[14px] text-ink-400"><span className="animate-pulse">Opening the agreement…</span></p>
            : (
              <div className="space-y-2 sm:space-y-3">
                {pages.map((src, i) => {
                  const last = i === pages.length - 1;
                  const page = (
                    <div className="@container relative bg-white shadow-sm" style={last && zoom ? { width: '260%' } : undefined}>
                      <img src={src} alt={`Page ${i + 1} of ${pages.length}`} className="block w-full" />
                      {last && overlay}
                    </div>
                  );
                  return <div key={i} className={last && zoom ? 'overflow-x-auto overscroll-x-contain' : undefined}>{page}</div>;
                })}
              </div>
            )}
      </div>
      {/* Under the pages: how many there are and, on a phone with a box to sign, the zoom. */}
      {pages && (
        <div className="flex min-h-[48px] items-center justify-between gap-3 border-t border-ink-200 bg-white px-4 text-[13px] text-ink-500">
          <span>{pages.length} page{pages.length === 1 ? '' : 's'}</span>
          {overlay && <button type="button" onClick={toggleZoom} className="inline-flex min-h-[44px] items-center font-medium text-bronze-700 underline-offset-4 hover:underline sm:hidden">{zoom ? 'Fit page to screen' : 'Zoom in to sign'}</button>}
        </div>
      )}
    </div>
  );
}

/**
 * The tenant's signature box on the agreement's last page, signed in place: a canvas laid over the signature area.
 * Mouse, finger or pen; the PNG goes to the server, which stamps it into the PDF in the same place. With `typed`
 * set, the box shows the name in italic instead (a typed signature).
 */
export function SignatureBox({ typed, onChange }: { typed: string | null; onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);
  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => { const c = ref.current!; const r = c.getBoundingClientRect(); return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) }; };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => { const c = ref.current!; const ctx = c.getContext('2d')!; drawing.current = true; c.setPointerCapture(e.pointerId); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = ref.current!.getContext('2d')!; const { x, y } = pos(e);
    ctx.lineWidth = 5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1d1b18'; ctx.lineTo(x, y); ctx.stroke();
    if (!dirty) setDirty(true);
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current!.toDataURL('image/png')); };
  const clear = () => { const c = ref.current; if (c) c.getContext('2d')!.clearRect(0, 0, c.width, c.height); setDirty(false); onChange(null); };
  const s = SIGN_AT.signature;
  return (
    <>
      <div data-sign-box className={`absolute ${typed ? '' : `rounded-sm ${dirty ? 'ring-1 ring-bronze-300' : 'bg-bronze-50/60 ring-2 ring-bronze-400'}`}`}
        style={{ left: `${s.left}%`, top: `${s.top}%`, width: `${s.width}%`, height: `${s.height}%` }}>
        {typed
          ? <span className="absolute bottom-[0.5cqw] left-[0.5cqw] max-w-full truncate font-serif italic leading-none text-ink-900" style={{ fontSize: '2.9cqw' }}>{typed}</span>
          : <>
              <canvas ref={ref} width={630} height={186} className="absolute inset-0 h-full w-full cursor-crosshair touch-none" onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} />
              {!dirty && <span className="pointer-events-none absolute inset-0 grid place-items-center font-medium text-bronze-700" style={{ fontSize: '1.9cqw' }}>Sign here</span>}
            </>}
      </div>
      {!typed && dirty && (
        <button type="button" onClick={clear} className="absolute text-right text-ink-500 underline hover:text-ink-900"
          style={{ left: `${SIGN_AT.tools.left}%`, top: `${SIGN_AT.tools.top}%`, width: `${SIGN_AT.tools.width}%`, fontSize: '1.6cqw' }}>Clear</button>
      )}
      {(typed || dirty) && <span className="pointer-events-none absolute leading-none text-ink-900" style={{ left: `${SIGN_AT.date.left}%`, top: `${SIGN_AT.date.top}%`, fontSize: '1.8cqw' }}>{new Date().toLocaleDateString('en-GB')}</span>}
    </>
  );
}
